import { useState, useEffect } from 'react';
import { fetchNearbyTimeCapsules } from '../services/timeCapsuleService';
import { distanceBetween } from 'geofire-common';

import IconTimeCapsuleLockedMarker from '../assets/icons/icon-time-capsule-locked.png';
import IconTimeCapsuleUnlockedMarker from '../assets/icons/icon-time-capsule-unlocked.png';
import IconTreasureOpenedMarker from '../assets/icons/icon-treasure-open-marker.png';
import IconTreasureClosedMarker from '../assets/icons/icon-treasure-closed-marker.svg';
import IconMarkerPublic from '../assets/icons/icon-marker-public.svg';

/**
 * Hook to load capsule markers and pick the nearest capsule.
 */
export default function useTimeCapsuleMarkers(coords, { radius = 1000 } = {}) {
    const [markers, setMarkers] = useState([]);
    const [nearest, setNearest] = useState(null);

    useEffect(() => {
        if (!coords) return;
        let mounted = true;

        (async () => {
            const capsules = await fetchNearbyTimeCapsules({ center: coords, radius });
            if (!mounted) return;

            // Determine the nearest capsule
            let bestCapsule = null;
            capsules.forEach((cap) => {
                if (!bestCapsule) {
                    bestCapsule = cap;
                } else {
                    const prevDistance = distanceBetween(
                        [coords.lat, coords.lng],
                        [bestCapsule.location.lat, bestCapsule.location.lng]
                    );
                    const currDistance = distanceBetween(
                        [coords.lat, coords.lng],
                        [cap.location.lat, cap.location.lng]
                    );
                    if (currDistance < prevDistance) {
                        bestCapsule = cap;
                    }
                }
            });
            setNearest(bestCapsule);

            // Build marker items with appropriate icons
            const now = Date.now();
            const items = capsules.map((cap) => {
                const revealTs = cap.revealDate?.toDate().getTime() ?? -Infinity;
                const endTs = cap.endDate?.toDate().getTime() ?? Infinity;

                let iconSrc;
                // private: locked if revealDate in the future, otherwise unlocked
                if (cap.capsuleType === 'private') {
                    if (revealTs > now) {
                        iconSrc = IconTimeCapsuleLockedMarker;
                    } else {
                        iconSrc = IconTimeCapsuleUnlockedMarker;
                    }
                }
                // memberExclusive: opened if within reveal and end window
                else if (
                    cap.capsuleType === 'memberExclusive' &&
                    revealTs <= now &&
                    endTs > now
                ) {
                    iconSrc = IconTreasureOpenedMarker;
                }
                // memberExclusive: closed if endDate has passed
                else if (
                    cap.capsuleType === 'memberExclusive' &&
                    endTs <= now
                ) {
                    iconSrc = IconTreasureClosedMarker;
                }
                // public or sponsored: public icon if within reveal and end window
                else if (
                    (cap.capsuleType === 'public' || cap.capsuleType === 'sponsored') &&
                    revealTs <= now &&
                    endTs > now
                ) {
                    iconSrc = IconMarkerPublic;
                }
                // fallback to public icon
                else {
                    iconSrc = IconMarkerPublic;
                }

                const size = cap.capsuleType === 'private'
                    ? { width: 40, height: 75 }
                    : { width: 60, height: 60 };

                return {
                    id: cap.id,
                    position: { lat: cap.location.lat, lng: cap.location.lng },
                    title: cap.messageBody,
                    snippet: cap.location.address,
                    icon: iconSrc,
                    ...size,
                };
            });

            setMarkers(items);
        })();

        return () => {
            mounted = false;
        };
    }, [coords, radius]);

    return { markers, nearest };
}
